import {
  Component,
  Input,
  Output,
  OnInit,
  EventEmitter,
  OnDestroy,
  AfterViewInit,
} from "@angular/core"
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms"
import { Subscription } from "rxjs"
import { IntakeFormData } from "src/app/models/intakeFormData"

@Component({
  selector: "c1-privacy-intake-form",
  templateUrl: "./intake-form.component.html",
  styleUrls: ["./intake-form.component.scss"],
  standalone: false,
})
export class IntakeFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() showRemoveButton: boolean = true
  @Input() violatedHSHD: { [key: string]: string[] } = {}
  @Output() onRemoved: EventEmitter<null> = new EventEmitter<null>()
  @Output() onFormChanged: EventEmitter<{ isValid: boolean; hasDirtyAndInvalidInputs: boolean }> = new EventEmitter<{
    isValid: boolean
    hasDirtyAndInvalidInputs: boolean
  }>()
  formValueChangeSubscription: Subscription

  intakeFormGroup: UntypedFormGroup

  formId: string

  constructor(private formBuilder: UntypedFormBuilder) {}

  ngOnInit(): void {
    this.formId = Array.from(crypto.getRandomValues(new Uint8Array(9)), b => b.toString(36)).join('');
    this.intakeFormGroup = this.formBuilder.group({
      intakeGroup: this.formBuilder.group({
        fileName: ["", [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
        field: ["", [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
        incorrectValue: ["", [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
        correctValue: ["", [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
        explanation: ["", [Validators.required, Validators.minLength(1), Validators.maxLength(250)]],
      }),
    })
  }

  ngAfterViewInit(): void {
    this.formValueChangeSubscription = this.intakeFormGroup.get("intakeGroup").valueChanges.subscribe((form) => {
      this.trimInputs()
      const isValid = this.intakeFormGroup.get("intakeGroup").valid
      const hasDirtyAndInvalidInputs =
        this.showError("fileName", this.formId) ||
        this.showError("field", this.formId) ||
        this.showError("incorrectValue", this.formId) ||
        this.showError("correctValue", this.formId) ||
        this.showError("explanation", this.formId)

      this.onFormChanged.emit({ isValid, hasDirtyAndInvalidInputs })
    })
  }

  private getFormControl(controlName: string) {
    return this.intakeFormGroup.get(["intakeGroup", controlName]);
  }

  public setFormData(data: IntakeFormData) {
    const intakeGroup = this.intakeFormGroup.get("intakeGroup")
    const fileName = intakeGroup.get("fileName")
    const field = intakeGroup.get("field")
    const incorrectValue = intakeGroup.get("incorrectValue")
    const correctValue = intakeGroup.get("correctValue")
    const explanation = intakeGroup.get("explanation")

    setTimeout(() => {
      fileName.setValue(data.fileName)
      field.setValue(data.field)
      incorrectValue.setValue(data.incorrectValue)
      correctValue.setValue(data.correctValue)
      explanation.setValue(data.explanation)
    })
  }

  public getValue(controlName: string) {
    return this.getFormControl(controlName)?.value
  }

  trimInputs() {
    const controls = [
      { name: "fileName", maxLength: 100 },
      { name: "field", maxLength: 100 },
      { name: "incorrectValue", maxLength: 100 },
      { name: "correctValue", maxLength: 100 },
      { name: "explanation", maxLength: 250 },
    ];

    controls.forEach(({ name, maxLength }) => {
      const control = this.getFormControl(name);
      if (control && control.value.length > maxLength) {
        control.setValue(control.value.slice(0, maxLength));
      }
    });
  }

  public hasError(controlName: string, formId: string): boolean {
    if (this.formId !== formId) {
      return false;
    }
    return this.showError(controlName, formId) || !!this.violatedHSHD?.[`${controlName}-${formId}`];
  }

  public showError(controlName: string, formId: string): boolean {
    if (this.formId !== formId) {
      return false;
    }
    const control = this.getFormControl(controlName);
    return control?.invalid && control?.dirty;
  }

  public getInputLength(controlName: string): number {
    return this.getFormControl(controlName)?.value?.length || 0;
  }

  removeForm() {
    this.onRemoved.emit()
  }

  public focusFirstError(): boolean {
    const controlNames = ["fileName", "field", "incorrectValue", "correctValue", "explanation"]
    for (const name of controlNames) {
      if (this.hasError(name, this.formId)) {
        const inputEl = document.getElementById(`${name}Input-${this.formId}`) as HTMLElement | null
        if (inputEl) {
          inputEl.focus()
          return true
        }
      }
    }
    return false
  }

  ngOnDestroy(): void {
    this.formValueChangeSubscription?.unsubscribe();
  }
}
